// ═══════════════════════════════════════════════════════════════
// CustomerWizard — 3-step modal for "I just got a new customer
// and want to capture them, their vehicle, and an appointment in
// one flow." Solves Prayer's #1 daily pain (per 2026-05 transcript):
//
//   "Customer information, customer vehicle, scheduled date. The
//    third step can be completed or ignored for later, but most of
//    the time, it's going to be completed right there."
//
// Steps:
//   1. Customer (required — at least a name)
//   2. Vehicle  (skippable — if any field filled, year/make/model required)
//   3. Schedule (skippable — if any field filled, date+time+duration required)
//
// Launched from:
//   - Home's + NEW → Customer choice (no slot pre-fill)
//   - Calendar's CustomerPicker → "+ New customer" option (step 3 pre-
//     filled with the slot's date/time/duration that the user tapped)
//
// On finish: creates the customer, the vehicle (if filled), and a draft
// job with the schedule fields (if filled). Calls onComplete with the
// new IDs so the calendar can refresh / select the customer.
//
// NOTE: the customer + vehicle form bodies duplicate fields with
// CustomerModal and Vehicles' VehicleModal. Extraction into shared
// form-body components is a follow-up — kept inline here to ship the
// flow faster without churning the existing single-step modals.
// ═══════════════════════════════════════════════════════════════
import React, { useState } from 'react';
import { Timestamp } from 'firebase/firestore';
import {
  ArrowLeft, ArrowRight, Check, X, Plus,
  User, Car, CalendarPlus,
} from 'lucide-react';
import { customersCol, vehiclesCol, jobsCol } from './database.js';
import { createDoc, createJobDoc } from './lib/db-crud.js';
import { CONTACT_METHODS } from './CustomerModal.jsx';

const DEFAULT_DURATION = 60;

function pad2(n) { return String(n).padStart(2, '0'); }
function dateInputValue(d) { return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`; }
function dateFromInputValue(s) {
  const [y, m, day] = s.split('-').map(Number);
  return new Date(y, m - 1, day);
}
function combineDateAndTime(date, hhmm) {
  const [h, m] = hhmm.split(':').map(Number);
  const x = new Date(date);
  x.setHours(h, m, 0, 0);
  return x;
}

function blankCustomerForm() {
  return {
    name: '', phone: '', email: '', address: '', notes: '',
    preferredContact: [],
  };
}
function blankVehicleForm() {
  return {
    year: '', make: '', model: '', trim: '',
    color: '', plate: '', vin: '', odometer: '',
  };
}
function blankScheduleForm(initial) {
  return {
    date: initial?.date || '',
    time: initial?.time || '',
    duration: initial?.duration || DEFAULT_DURATION,
    description: '',
  };
}

export default function CustomerWizard({ initialSchedule, onComplete, onClose }) {
  const [step, setStep] = useState(1);
  const [customer, setCustomer] = useState(blankCustomerForm());
  const [vehicle, setVehicle]   = useState(blankVehicleForm());
  const [schedule, setSchedule] = useState(blankScheduleForm(initialSchedule));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Step navigation -------------------------------------------------
  function goBack() { setError(null); setStep((s) => Math.max(1, s - 1)); }
  function goNext() {
    setError(null);
    // Validate current step before advancing.
    if (step === 1) {
      if (!customer.name.trim()) { setError('Customer name is required.'); return; }
    }
    if (step === 2 && hasVehicleData()) {
      if (!vehicle.year || !vehicle.make.trim() || !vehicle.model.trim()) {
        setError('Year, make, and model are required if you fill any vehicle info. Or hit Skip to add the vehicle later.');
        return;
      }
    }
    setStep((s) => Math.min(3, s + 1));
  }
  function skipStep() {
    setError(null);
    if (step === 2) setVehicle(blankVehicleForm());
    if (step === 3) setSchedule(blankScheduleForm()); // discard, even pre-fill
    setStep((s) => Math.min(3, s + 1));
  }

  // Has-any-data probes — drive validation + save decisions ----------
  function hasVehicleData() {
    return !!(vehicle.year || vehicle.make.trim() || vehicle.model.trim() ||
              vehicle.trim.trim() || vehicle.color.trim() || vehicle.plate.trim() ||
              vehicle.vin.trim() || vehicle.odometer);
  }
  function hasScheduleData() {
    return !!(schedule.date || schedule.time || (schedule.description || '').trim());
  }

  // Finish — save customer, then vehicle (if data), then job (if data)
  async function handleFinish() {
    setError(null);

    // Final validations
    if (!customer.name.trim()) { setError('Customer name is required.'); setStep(1); return; }
    if (hasVehicleData()) {
      if (!vehicle.year || !vehicle.make.trim() || !vehicle.model.trim()) {
        setError('Year, make, and model are required on the vehicle.'); setStep(2); return;
      }
    }
    if (hasScheduleData()) {
      if (!schedule.date || !schedule.time) {
        setError('Pick a date and time for the appointment, or skip the schedule.'); setStep(3); return;
      }
    }

    setSubmitting(true);
    try {
      // 1. Customer
      const customerPayload = {
        name: customer.name.trim(),
        phone: customer.phone.trim(),
        email: customer.email.trim(),
        address: customer.address.trim(),
        notes: customer.notes.trim(),
        preferredContact: Array.isArray(customer.preferredContact) ? customer.preferredContact : [],
      };
      // createDoc() resolves to the new document's ID string.
      const customerId = await createDoc(customersCol(), customerPayload);

      // 2. Vehicle (if filled)
      let vehicleId = null;
      if (hasVehicleData()) {
        const vehiclePayload = {
          customerId,
          year: Number(vehicle.year),
          make: vehicle.make.trim(),
          model: vehicle.model.trim(),
          trim: vehicle.trim.trim() || null,
          color: vehicle.color.trim() || null,
          plate: vehicle.plate.trim().toUpperCase() || null,
          vin: vehicle.vin.trim().toUpperCase() || null,
          odometer: vehicle.odometer ? Number(vehicle.odometer) : null,
          customFields: [],
        };
        vehicleId = await createDoc(vehiclesCol(), vehiclePayload);
      }

      // 3. Scheduled job (if filled)
      let jobId = null;
      if (hasScheduleData()) {
        const startDate = combineDateAndTime(dateFromInputValue(schedule.date), schedule.time);
        const duration = Number(schedule.duration) || DEFAULT_DURATION;
        const jobPayload = {
          customerId,
          vehicleId, // may be null if no vehicle was added
          status: 'draft',
          description: (schedule.description || '').trim() || 'Scheduled appointment',
          totalPrice: 0,
          scheduledAt: Timestamp.fromDate(startDate),
          durationMinutes: duration,
          lineItems: [],
          shareId: null, invoiceShareId: null,
          sentAt: null, approvedAt: null, completedAt: null, paidAt: null,
        };
        jobId = await createJobDoc(jobsCol(), jobPayload);
      }

      onComplete?.({ customerId, vehicleId, jobId });
      onClose();
    } catch (e) {
      console.error('Wizard save failed:', e);
      setError(e.message);
      setSubmitting(false);
    }
  }

  // ─── Render ─────────────────────────────────────────────────────
  return (
    <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal-box wizard-box" role="dialog" aria-modal="true">
        <div className="modal-header">
          <h2 className="wizard-title">
            {step === 1 && <><User size={18} /> New customer</>}
            {step === 2 && <><Car size={18} /> Their vehicle</>}
            {step === 3 && <><CalendarPlus size={18} /> Schedule</>}
          </h2>
          <button type="button" className="btn-link" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        {/* Step indicator dots */}
        <div className="wizard-steps" role="tablist" aria-label="Wizard steps">
          {[1, 2, 3].map((n) => (
            <div
              key={n}
              className={`wizard-step ${step === n ? 'active' : ''} ${step > n ? 'done' : ''}`}
              role="tab"
              aria-selected={step === n}
            >
              <span className="wizard-step-num">{step > n ? <Check size={12} strokeWidth={3} /> : n}</span>
              <span className="wizard-step-label">
                {n === 1 ? 'Customer' : n === 2 ? 'Vehicle' : 'Schedule'}
              </span>
            </div>
          ))}
        </div>

        <div className="wizard-body">
          {step === 1 && <CustomerStep form={customer} setForm={setCustomer} />}
          {step === 2 && <VehicleStep form={vehicle} setForm={setVehicle} />}
          {step === 3 && <ScheduleStep form={schedule} setForm={setSchedule} />}
        </div>

        {error && <p className="form-error" style={{ margin: '0 var(--space-5)' }}>{error}</p>}

        <div className="wizard-footer">
          {step > 1 ? (
            <button type="button" className="btn-secondary" onClick={goBack} disabled={submitting}>
              <ArrowLeft size={14} /> Back
            </button>
          ) : (
            <button type="button" className="btn-link" onClick={onClose} disabled={submitting}>
              Cancel
            </button>
          )}
          <div className="wizard-footer-right">
            {(step === 2 || step === 3) && (
              <button type="button" className="btn-link" onClick={skipStep} disabled={submitting}>
                Skip {step === 2 ? 'vehicle' : 'schedule'}
              </button>
            )}
            {step < 3 ? (
              <button type="button" className="btn" onClick={goNext} disabled={submitting}>
                Next <ArrowRight size={14} />
              </button>
            ) : (
              <button type="button" className="btn" onClick={handleFinish} disabled={submitting}>
                {submitting ? 'Saving…' : (<><Plus size={14} strokeWidth={2.5} /> Finish</>)}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Step 1: Customer ─────────────────────────────────────────────
function CustomerStep({ form, setForm }) {
  function update(field, value) { setForm((f) => ({ ...f, [field]: value })); }
  function togglePref(method) {
    setForm((f) => {
      const current = Array.isArray(f.preferredContact) ? f.preferredContact : [];
      const next = current.includes(method)
        ? current.filter((m) => m !== method)
        : [...current, method];
      return { ...f, preferredContact: next };
    });
  }
  const preferred = Array.isArray(form.preferredContact) ? form.preferredContact : [];

  return (
    <div className="form">
      <label className="form-row">
        <span>Name *</span>
        <input
          type="text" required maxLength={80} autoFocus
          value={form.name} onChange={(e) => update('name', e.target.value)}
          placeholder="First + last initial (e.g., Bob T.)"
        />
      </label>
      <label className="form-row">
        <span>Phone</span>
        <input
          type="tel" maxLength={20} inputMode="tel"
          value={form.phone} onChange={(e) => update('phone', e.target.value)}
          placeholder="(913) 555-1234"
        />
      </label>
      <label className="form-row">
        <span>Email</span>
        <input
          type="email" maxLength={120} inputMode="email"
          value={form.email} onChange={(e) => update('email', e.target.value)}
          placeholder="bob@example.com"
        />
      </label>
      <label className="form-row">
        <span>Address</span>
        <input
          type="text" maxLength={200}
          value={form.address} onChange={(e) => update('address', e.target.value)}
          placeholder="123 Maple St, Overland Park, KS"
        />
      </label>
      <div className="form-row">
        <span>Preferred contact</span>
        <div className="checkbox-group">
          {CONTACT_METHODS.map((m) => (
            <label key={m.value} className="checkbox-row">
              <input
                type="checkbox"
                checked={preferred.includes(m.value)}
                onChange={() => togglePref(m.value)}
              />
              {m.label}
            </label>
          ))}
        </div>
      </div>
      <label className="form-row">
        <span>Notes</span>
        <textarea
          maxLength={1000} rows={3}
          value={form.notes} onChange={(e) => update('notes', e.target.value)}
          placeholder="Garage code, gate access, anything to remember…"
        />
      </label>
    </div>
  );
}

// ─── Step 2: Vehicle ──────────────────────────────────────────────
function VehicleStep({ form, setForm }) {
  function update(field, value) { setForm((f) => ({ ...f, [field]: value })); }
  return (
    <div className="form">
      <p className="muted small wizard-step-hint">
        Or hit <strong>Skip vehicle</strong> below if you don't have the vehicle info yet.
      </p>
      <div className="form-row-pair">
        <label className="form-row" style={{ flex: '0 0 100px' }}>
          <span>Year *</span>
          <input
            type="number" min={1900} max={new Date().getFullYear() + 2} inputMode="numeric"
            value={form.year} onChange={(e) => update('year', e.target.value)}
            placeholder="2018" autoFocus
          />
        </label>
        <label className="form-row" style={{ flex: 1 }}>
          <span>Make *</span>
          <input
            type="text" maxLength={40}
            value={form.make} onChange={(e) => update('make', e.target.value)}
            placeholder="Ford"
          />
        </label>
      </div>
      <div className="form-row-pair">
        <label className="form-row" style={{ flex: 1 }}>
          <span>Model *</span>
          <input
            type="text" maxLength={40}
            value={form.model} onChange={(e) => update('model', e.target.value)}
            placeholder="F-150"
          />
        </label>
        <label className="form-row" style={{ flex: 1 }}>
          <span>Trim</span>
          <input
            type="text" maxLength={40}
            value={form.trim} onChange={(e) => update('trim', e.target.value)}
            placeholder="XLT"
          />
        </label>
      </div>
      <div className="form-row-pair">
        <label className="form-row" style={{ flex: 1 }}>
          <span>Color</span>
          <input
            type="text" maxLength={20}
            value={form.color} onChange={(e) => update('color', e.target.value)}
            placeholder="Silver"
          />
        </label>
        <label className="form-row" style={{ flex: 1 }}>
          <span>Plate</span>
          <input
            type="text" maxLength={12}
            value={form.plate} onChange={(e) => update('plate', e.target.value)}
            placeholder="ABC-1234"
          />
        </label>
      </div>
      <label className="form-row">
        <span>VIN</span>
        <input
          type="text" maxLength={17}
          value={form.vin} onChange={(e) => update('vin', e.target.value)}
          placeholder="1FTFW1ET5DFC10312"
          style={{ textTransform: 'uppercase', fontFamily: 'var(--font-mono)' }}
        />
      </label>
      <label className="form-row">
        <span>Odometer (mi)</span>
        <input
          type="number" min={0} inputMode="numeric"
          value={form.odometer} onChange={(e) => update('odometer', e.target.value)}
          placeholder="85000"
        />
      </label>
      <p className="muted small">
        You can add color/plate/VIN/odometer/custom fields later from the customer page.
      </p>
    </div>
  );
}

// ─── Step 3: Schedule ─────────────────────────────────────────────
function ScheduleStep({ form, setForm }) {
  function update(field, value) { setForm((f) => ({ ...f, [field]: value })); }
  // Default to today if user starts filling time before date
  function ensureDateDefault() {
    if (!form.date) update('date', dateInputValue(new Date()));
  }
  return (
    <div className="form">
      <p className="muted small wizard-step-hint">
        Set the appointment now, or hit <strong>Skip schedule</strong> — you can add it from the calendar later.
        Price gets set when you send the estimate.
      </p>
      <div className="form-row-pair">
        <label className="form-row" style={{ flex: 1 }}>
          <span>Date</span>
          <input
            type="date"
            value={form.date}
            onChange={(e) => update('date', e.target.value)}
          />
        </label>
        <label className="form-row" style={{ flex: 1 }}>
          <span>Start time</span>
          <input
            type="time" step="900"
            value={form.time}
            onChange={(e) => { update('time', e.target.value); ensureDateDefault(); }}
          />
        </label>
        <label className="form-row" style={{ flex: '0 0 110px' }}>
          <span>Duration (min)</span>
          <input
            type="number" min={15} step={15} inputMode="numeric"
            value={form.duration}
            onChange={(e) => update('duration', e.target.value)}
          />
        </label>
      </div>
      <label className="form-row">
        <span>What needs done?</span>
        <textarea
          maxLength={500} rows={3}
          value={form.description}
          onChange={(e) => update('description', e.target.value)}
          placeholder="e.g., Brake inspection, oil change, fuel pump replacement"
        />
      </label>
    </div>
  );
}
